import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import type { StripeCardElement, SetupIntent, Stripe, SetupIntentResult, PaymentMethod } from '@stripe/stripe-js'
import apiClient from '../../apis/apiClient'

export interface Card {
    brand: string
    created: string
    default: boolean
    exp: string
    id: string
    last4: string
}

@Module({
    name: 'CardsModule',
    namespaced: true,
})
export default class CardsModule extends VuexModule {
    private _error: string | null = null

    //existing cards
    private _cards: Card[] = []

    //new card
    private _stripeCardElement: StripeCardElement | null = null
    private _intent: SetupIntent | null = null
    private _intentResponse: SetupIntentResult | null = null

    private _isLoading: boolean = false

    //used for subscribing after adding a new card
    private _currentCardId: string | PaymentMethod | null = null

    //stripe library
    private _stripeInstance: Stripe | undefined = undefined
    private _cardError: string | null | undefined = null
    private _showCardForm: boolean = false
    private _isPaymentMethodComplete: boolean = false

    get error() {
        return this._error
    }

    get defaultCardId() {
        const card = this._cards.find((card: Card) => !!card.default)
        return card?.id
    }

    get hasValidCardInfo() {
        return this._isPaymentMethodComplete && !this._cardError
        // The error used to be checked like this:
        //  this._cardError?.trim().length == 0
    }

    // TODO: See why this was not set in getters in the legacy code
    get cards() {
        return this._cards
    }

    get intent() {
        return this._intent
    }

    get intentResponse() {
        return this._intentResponse
    }

    get isLoading() {
        return this._isLoading
    }

    get currentCardId() {
        return this._currentCardId
    }

    get stripeInstance() {
        return this._stripeInstance
    }

    get cardError() {
        return this._cardError
    }

    get showCardForm() {
        return this._showCardForm
    }

    get stripeCardElement() {
        return this._stripeCardElement
    }

    get isPaymentMethodComplete() {
        return this._isPaymentMethodComplete
    }

    @Mutation
    setError(error: string | null) {
        this._error = error
    }

    @Mutation
    setCards(cards: Card[]) {
        this._cards = cards
    }

    @Mutation
    setStripeCardElement(stripeCardElement: StripeCardElement | null) {
        this._stripeCardElement = stripeCardElement
    }

    @Mutation
    setIntent(intent: SetupIntent | null) {
        this._intent = intent
    }

    @Mutation
    setIntentResponse(intent_response: SetupIntentResult | null) {
        this._intentResponse = intent_response
    }

    @Mutation
    setIsLoading(isLoading: boolean) {
        this._isLoading = isLoading
    }

    @Mutation
    setCurrentCardId(cardId: string | PaymentMethod | null) {
        this._currentCardId = cardId
    }

    @Mutation
    setStripeInstance(stripeInstance: Stripe) {
        this._stripeInstance = stripeInstance
    }

    @Mutation
    setCardError(cardError: string | null | undefined) {
        this._cardError = cardError
    }

    @Mutation
    setShowCardForm(showCardForm: boolean) {
        this._showCardForm = showCardForm
    }

    @Mutation
    setIsPaymentMethodComplete(isPaymentMethodComplete: boolean) {
        this._isPaymentMethodComplete = isPaymentMethodComplete
    }

    @Action
    async obtainIntent() {
        try {
            this.setError(null)

            const { data } = await apiClient.get('/card/intent')

            this.setIntent(data)
        } catch (error: any) {
            console.error('Error in obtainIntent action:', error)
            this.setError(error)
        }
    }

    @Action
    async getPaymentCards() {
        try {
            this.setIsLoading(true)
            this.setError(null)

            const { data } = await apiClient.get('/account/user/cards')

            this.setCards(data)
        } catch (error: any) {
            console.error('Error in getPaymentCards action:', error)
            this.setError(error)
        } finally {
            this.setIsLoading(false)
        }
    }

    @Action
    async setDefaultPaymentCard(cardId: string) {
        try {
            this.setIsLoading(true)
            this.setError(null)

            await apiClient.post('/account/user/card/set-default', {
                method: cardId,
            })
        } catch (error: any) {
            console.error('Error in setDefaultPaymentCard action:', error)
            this.setError(error)
        } finally {
            this.setIsLoading(false)
        }
    }

    @Action
    async addPaymentCard() {
        // If we do not have a setupIntent key do not proceed, this means there is an error on the card and it should be displayed
        if (typeof this._intentResponse?.setupIntent === 'undefined') {
            throw new Error('Invalid card information')
        }

        try {
            this.setIsLoading(true)
            this.setError(null)

            await apiClient.post('/card', {
                method: this._intentResponse.setupIntent.payment_method,
            })

            this.setCurrentCardId(this._intentResponse.setupIntent.payment_method)
        } catch (error: any) {
            console.error('Error in addPaymentCard action:', error)
            this.setError(error)
        } finally {
            this.setIsLoading(false)
        }
    }

    @Action
    async removePaymentCard(cardId: string) {
        try {
            this.setIsLoading(true)
            this.setError(null)

            await apiClient.delete('account/user/card', {
                data: {
                    method: cardId,
                },
            })
        } catch (error: any) {
            console.error('Error in removePaymentCard action:', error)
            this.setError(error)
        } finally {
            // Note: I've added this to ensure loading state is reset in the success case too
            // Remove if you're handling this elsewhere
            this.setIsLoading(false)
        }
    }

    @Action
    async confirmCardSetup() {
        if (!this.stripeInstance) return
        if (!this._intent?.client_secret) return
        if (!this._stripeCardElement) return

        try {
            this.setError(null)

            const intentResponse: SetupIntentResult = await this.stripeInstance.confirmCardSetup(
                this._intent.client_secret,
                {
                    payment_method: {
                        card: this._stripeCardElement,
                    },
                },
            )

            if (intentResponse.error) {
                this.setCardError(intentResponse.error.message)
                this.setIsPaymentMethodComplete(false)
                return
            }

            this.setIntentResponse(intentResponse)
        } catch (error: any) {
            console.error('Error in confirmCardSetup action:', error)
            this.setError(error)
        }
    }
}
